// Pure rules for the store's screens with the kitchen flow on (batch 4, lane
// store): which group a tab belongs to, the quiet "no stock levels" rule, the
// To do order and row words, and "Approve & send what we have".
import type { InventoryTab } from '@/components/store/inventoryRouteSearch'
import type { StockAlert } from '@/types'
import { fmtQty } from '@/lib/quantity'
import { formatDateHuman } from '@/lib/utils'
import { storeUsable } from './drafts'
import { statusMeta } from './strings'
import { ST } from './storeScreenStrings'
import type { Requisition, RequisitionLine } from './types'

const EPS = 1e-6

// ─── Tabs ───────────────────────────────────────────────────────────────────

/** The five top-level tabs. Each old tab id still lives in the URL (`?tab=`),
 *  so every deep link keeps working; the group is derived from it. */
export type StoreGroup = 'todo' | 'stock' | 'buying' | 'count' | 'more'

export const STORE_GROUPS: StoreGroup[] = ['todo', 'stock', 'buying', 'count', 'more']

export function groupOfTab(tab: InventoryTab): StoreGroup {
  switch (tab) {
    case 'requests':
      return 'todo'
    case 'items':
      return 'stock'
    case 'purchasing':
    case 'vendors':
      return 'buying'
    case 'count':
      return 'count'
    default:
      return 'more'
  }
}

/** Where a group's tab button lands. */
export const GROUP_HOME: Record<Exclude<StoreGroup, 'more'>, InventoryTab> = {
  todo: 'requests',
  stock: 'items',
  buying: 'purchasing',
  count: 'count',
}

/** What sits under "More", in order. Locations is for an admin only. */
export function moreTabs(isAdmin: boolean): InventoryTab[] {
  return ['alerts', 'history', 'reports', 'waste', 'basics', ...(isAdmin ? (['locations'] as InventoryTab[]) : [])]
}

/** The tab a store person lands on with no `?tab=`: the store's main job
 *  (To do) for the inventory manager, as today for everyone else. */
export function defaultStoreTab(kitchenFlow: boolean, role: string): InventoryTab {
  return kitchenFlow && role === 'inventory_manager' ? 'requests' : 'items'
}

// ─── Stock levels ───────────────────────────────────────────────────────────

/** An alert only because the item has no reorder level and nothing on hand:
 *  not a real warning, just an item nobody has set levels for. Oversold
 *  stock (below zero) is always a real warning. */
export function isNoLevelAlert(a: Pick<StockAlert, 'reorder_level' | 'quantity_on_hand'>): boolean {
  return !(a.reorder_level > EPS) && a.quantity_on_hand >= -1e-5
}

export function realAlerts<A extends Pick<StockAlert, 'reorder_level' | 'quantity_on_hand'>>(alerts: A[]): A[] {
  return alerts.filter((a) => !isNoLevelAlert(a))
}

/**
 * How many items to name in the quiet "no stock levels yet" note, or null for
 * no note. Shown when there are many: at least 10 items, or a fifth of all
 * items. `noLevelCount` comes from the stock summary; an older backend leaves
 * it out and the quiet alerts are counted instead.
 */
export function noLevelsNote(opts: { noLevelCount?: number | null; totalItems?: number | null; quietAlerts: number }): number | null {
  const n = opts.noLevelCount ?? opts.quietAlerts
  if (n <= 0) return null
  const total = opts.totalItems ?? 0
  if (n >= 10 || (total > 0 && n / total >= 0.2)) return n
  return null
}

// ─── To do list ─────────────────────────────────────────────────────────────

/** "YYYY-MM-DD" of a needed-by value, read as a calendar day (no time zone shift). */
function dayKey(v: string): string {
  return v.slice(0, 10)
}

function localDayKey(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/** Soonest needed first; requests with no date last; ties oldest first. */
export function sortByNeededBy(list: Requisition[]): Requisition[] {
  return [...list].sort((a, b) => {
    const an = a.needed_by ? dayKey(a.needed_by) : null
    const bn = b.needed_by ? dayKey(b.needed_by) : null
    if (an !== bn) {
      if (an == null) return 1
      if (bn == null) return -1
      return an < bn ? -1 : 1
    }
    return a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0
  })
}

export type NeededTone = 'overdue' | 'soon' | 'later' | 'none'

/** The needed-by date in plain words: "Needed tomorrow", "Needed Tue 28 Oct",
 *  "Was needed Sun 12 Oct" (overdue). */
export function neededWords(neededBy: string | null, now: Date = new Date()): { text: string; tone: NeededTone } {
  if (!neededBy) return { text: ST.todo.noDate, tone: 'none' }
  const key = dayKey(neededBy)
  const today = localDayKey(now)
  const tmr = new Date(now)
  tmr.setDate(now.getDate() + 1)
  const [y, m, d] = key.split('-').map(Number)
  const label = formatDateHuman(new Date(y, (m || 1) - 1, d || 1))
  if (key < today) return { text: ST.todo.wasNeeded(label), tone: 'overdue' }
  if (key === today) return { text: ST.todo.neededToday, tone: 'soon' }
  if (key === localDayKey(tmr)) return { text: ST.todo.neededTomorrow, tone: 'soon' }
  return { text: ST.todo.neededOn(label), tone: 'later' }
}

/** The request header: "Needed tomorrow, Sun 26 Oct" — the date added
 *  when the words alone ("today", "tomorrow") do not say it. */
export function neededHeader(neededBy: string | null, now: Date = new Date()): string {
  const w = neededWords(neededBy, now)
  if (!neededBy || w.tone !== 'soon') return w.text
  const [y, m, d] = dayKey(neededBy).split('-').map(Number)
  return `${w.text}, ${formatDateHuman(new Date(y, (m || 1) - 1, d || 1))}`
}

export type ChipTone = 'new' | 'wait' | 'move' | 'bad' | 'neutral' | 'good'

/** The status chip on a To do row. */
export function todoChip(r: Requisition, isAdmin: boolean): { label: string; tone: ChipTone } {
  if (r.has_shortage && !['closed', 'rejected', 'cancelled', 'draft'].includes(r.status)) {
    return { label: isAdmin ? ST.todo.chipMissingAdmin : ST.todo.chipMissingManager, tone: 'bad' }
  }
  if (r.kind === 'return' && r.awaiting_receipt && (r.status === 'issued' || r.status === 'received')) {
    return { label: ST.todo.chipReturn, tone: 'move' }
  }
  if (r.kind === 'issue' && r.status === 'submitted') return { label: ST.todo.chipNew, tone: 'new' }
  if (r.kind === 'issue' && (r.status === 'approved' || r.status === 'partially_issued') && (r.lines_to_send ?? 0) > 0) {
    return { label: ST.todo.chipToSend(r.lines_to_send as number), tone: 'wait' }
  }
  const m = statusMeta(r.status, r.kind)
  const tone: ChipTone = r.status === 'closed' ? 'good' : r.status === 'rejected' ? 'bad' : 'neutral'
  return { label: m.label, tone }
}

/** First line of a To do row. */
export function todoTitle(r: Requisition, now: Date = new Date()): { text: string; tone: NeededTone } {
  if (r.has_shortage && !['closed', 'rejected', 'cancelled', 'draft'].includes(r.status)) {
    return { text: ST.todo.missingTitle, tone: 'overdue' }
  }
  if (r.kind === 'return') return { text: ST.todo.returnTitle, tone: 'none' }
  return neededWords(r.needed_by, now)
}

// ─── Approve & send what we have ────────────────────────────────────────────

function round3(n: number): number {
  return Math.round(n * 1000) / 1000
}

/** Send now, pre-filled: what was asked, capped at what the store has in date. */
export function sendPrefill(l: RequisitionLine): number {
  return round3(Math.max(0, Math.min(l.requested_qty, storeUsable(l))))
}

/** How short the store is on a line before anything is typed. */
export function storeShortBy(l: RequisitionLine): number {
  return round3(Math.max(0, l.requested_qty - Math.max(storeUsable(l), 0)))
}

/** The warning under a short line: "Only 15 kg — 7.5 kg short". */
export function shortWords(l: RequisitionLine): string | null {
  const short = storeShortBy(l)
  if (short <= EPS) return null
  const has = Math.max(storeUsable(l), 0)
  return has <= EPS ? ST.send.noneShort(fmtQty(short), l.unit) : ST.send.onlyShort(fmtQty(has), fmtQty(short), l.unit)
}

/** "2 kg expired on Tue 7 Oct — not counted." or null. */
export function expiredWords(l: RequisitionLine): string | null {
  const q = l.source_expired_qty ?? 0
  if (q <= EPS || !l.source_expired_first) return null
  return ST.send.expiredNotCounted(fmtQty(q), l.unit, formatDateHuman(l.source_expired_first))
}

/** What is wrong with a Send now amount on a submitted request, or null. */
export function sendNowError(l: RequisitionLine, qty: number): string | null {
  if (!Number.isFinite(qty) || qty < 0) return ST.send.enterNumber
  if (qty > l.requested_qty + EPS) return ST.send.moreThanAsked(fmtQty(l.requested_qty), l.unit)
  if (qty > storeUsable(l) + EPS) return ST.send.moreThanStore(fmtQty(Math.max(storeUsable(l), 0)), l.unit)
  return null
}

/** What is wrong with a Send now amount on an approved / part-sent request, or null. */
export function sendRestError(l: RequisitionLine, qty: number): string | null {
  if (!Number.isFinite(qty) || qty < 0) return ST.send.enterNumber
  if (qty > l.to_issue_qty + EPS) return ST.send.moreThanOwed(fmtQty(l.to_issue_qty), l.unit)
  if (qty > storeUsable(l) + EPS) return ST.send.moreThanStore(fmtQty(Math.max(storeUsable(l), 0)), l.unit)
  return null
}

export interface SendSummary {
  /** Lines with something going now. */
  sending: number
  total: number
  /** Lines going short (still to come), with how much. */
  short: { name: string; qty: number; unit: string }[]
}

/** Against `owed` (asked on approve, still to send on the rest). */
export function sendSummary(lines: RequisitionLine[], qty: Record<string, number>, owed: (l: RequisitionLine) => number): SendSummary {
  const short: SendSummary['short'] = []
  let sending = 0
  for (const l of lines) {
    const v = qty[l.id] ?? 0
    if (v > EPS) sending++
    const gap = round3(owed(l) - v)
    if (gap > EPS) short.push({ name: l.item_name, qty: gap, unit: l.unit })
  }
  return { sending, total: lines.length, short }
}

/** "chicken 7.5 kg, yoghurt 6 kg" */
export function shortList(short: SendSummary['short']): string {
  return short.map((s) => `${s.name.toLowerCase()} ${fmtQty(s.qty)} ${s.unit}`).join(', ')
}

export function approveSendLabel(s: SendSummary): string {
  return s.sending === 0 ? ST.send.approveOnly : ST.send.approveSend(s.sending, s.total)
}

/** Body for POST …/approve-and-issue: approve every line as asked, send what is typed. */
export function approveAndIssueBody(lines: RequisitionLine[], qty: Record<string, number>) {
  return { lines: lines.map((l) => ({ line_id: l.id, qty: round3(qty[l.id] ?? 0) })) }
}

/**
 * Which rows to show in a long send table: every row that needs a look
 * (short, expired stock, or changed from the pre-fill) plus the first rows up
 * to `limit`; the rest fold into "+ N more items, all in stock".
 */
export function visibleSendRows(
  lines: RequisitionLine[],
  needsLook: (l: RequisitionLine) => boolean,
  expanded: boolean,
  limit = 6,
): { shown: RequisitionLine[]; hidden: number } {
  if (expanded || lines.length <= limit + 1) return { shown: lines, hidden: 0 }
  let room = Math.max(0, limit - lines.filter(needsLook).length)
  const shown = lines.filter((l) => {
    if (needsLook(l)) return true
    if (room > 0) {
      room--
      return true
    }
    return false
  })
  return { shown, hidden: lines.length - shown.length }
}
